import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Results } from "../../pages/Results";
import { usePage } from "../../routes";

import PublishIcon from "@mui/icons-material/Publish";
import { Stack } from "@mui/material";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import MuiStep from "@mui/material/Step";
import StepContent from "@mui/material/StepContent";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import Typography from "@mui/material/Typography";
import { Theme } from "@mui/material/styles";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";

import { HvSurveyableAssetGroup, PanelMeasurement, Result, ResultSurveyableAsset } from "../shared/interfaces/Result";

import { useResultResubmission } from "../data/useResultResubmission";

import { CustomerDropdown } from "../CustomerDropdown";
import { HVSubstationDropdown } from "../HVSubstationDropdown";
import { SwitchgearDropdown } from "../SwitchgearDropdown";

import { CompanyDropdown } from "../CompanyDropdown";
import { SiteDropdown } from "../SiteDropdown";
import { useChildMappingsByAssetIds } from "../data/Assets/useChildMappingByAssetIds";
import { useCompaniesByCustomerId } from "../data/useCompaniesByCustomerId";
import { useSitesByCompanyId } from "../data/useSitesByCompanyId";
import { MessageCard, MessageType } from "../shared/MessageCard";
import { MessageToast, ToastMode } from "../shared/MessageToast";
import { ProgressButton } from "../shared/ProgressButton";
import { getAssetTypeKey } from "../shared/constants/managedSurveyAssetTypes";
import { HvSubstation } from "../shared/interfaces/HvSubstation";
import { PartialResultPanel, PartialResultReducer, PartialResultSwitchgear, Step } from "./PartialResultReducer";

interface Props {
  partialResult: Result;
  partialResultReducer: PartialResultReducer;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
    },
    stepper: {
      backgroundColor: "inherit",
    },
    title: {
      display: "flex",
      justifyContent: "flex-start",
      fontSize: 16,
    },
    contentContainer: {
      marginLeft: theme.spacing(3),
    },
    buttonContainer: {
      maxWidth: "320px",
      marginTop: theme.spacing(2),
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      flexWrap: "wrap",
    },
    button: {
      margin: theme.spacing(1),
    },
    textButton: {
      textTransform: "uppercase",
    },
    noWrap: {
      whiteSpace: "nowrap",
    },
    panelCheckerCard: {
      width: "100%",
      maxWidth: "275px",
    },
    resubmissionStatusContainer: {
      width: "100%",
      display: "flex",
      justifyContent: "center",
    },
  }),
);

export function PartialResultStepper(props: Props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const resultPagePath = usePage(Results.name)?.path;

  const resultResubmission = useResultResubmission({
    onSuccess: onResubmitResultSuccess,
  });

  const [highlightingInvalidField, setHighlightingInvalidField] = useState(false);
  const [invalidSwitchgears, setInvalidSwitchgears] = useState<number[]>([]);

  const [selectedCompanyName, setSelectedCompanyName] = useState<string | undefined>();
  const [selectedSiteName, setSelectedSiteName] = useState<string | undefined>();

  const companies = useCompaniesByCustomerId(props.partialResultReducer.state.selectedCustomer?.id);

  const companyId = useMemo(() => {
    if (companies && selectedCompanyName) {
      return companies.data?.find((c) => c.name === selectedCompanyName)?.id;
    }
  }, [companies, selectedCompanyName]);

  const sites = useSitesByCompanyId(companyId);

  const siteId = useMemo(() => {
    if (sites.data && selectedSiteName) {
      return sites.data?.find((c) => c.name === selectedSiteName)?.id;
    }
  }, [sites.data, selectedSiteName]);

  const handleCompanyChange = useCallback(
    (selectedCompanyName: string) => {
      setSelectedCompanyName(selectedCompanyName);
      setSelectedSiteName(undefined);
    },
    [setSelectedCompanyName, setSelectedSiteName],
  );

  const handleSiteChange = useCallback(
    (selectedSiteName: string) => {
      setSelectedSiteName(selectedSiteName);
    },
    [setSelectedSiteName],
  );

  const createMultipleSwitchGears = (
    assets: ResultSurveyableAsset[],
    hvSurveyableAssetGroups: HvSurveyableAssetGroup[],
  ) =>
    assets.map((asset) => {
      const hvAsset = hvSurveyableAssetGroups.find(
        (hvAsset) => hvAsset.AssetName === asset.AssetName && hvAsset.AssetType === asset.AssetType,
      );
      return {
        name: asset.AssetName,
        panels: asset.PanelMeasurements.map((p) => ({
          panelAssetId: p.Panel360AssetId,
          panelNo: p.PanelNo,
          name: p.PanelName,
          isExcluded: false,
        })),
        switchGearManufacturer: hvAsset?.Manufacturer,
        assetType: asset.AssetType,
        ratedVoltage: hvAsset?.RatedVoltage,
        operatingVoltage: hvAsset?.OperatingVoltage,
        installDate: hvAsset && Date.parse(hvAsset.InstallDate) ? new Date(hvAsset.InstallDate) : undefined,
        insulation: hvAsset?.Insulation,
        switchGearType: hvAsset?.Type,
      };
    });

  const hvSubstationCreationPrePopulatedValues: Partial<HvSubstation> = {
    name: props.partialResult.dataValue.MetaData.SubstationGroup?.SubstationName,
    switchGears:
      props.partialResult.dataValue.MetaData.SwitchGearGroup != null
        ? [
            {
              name: `${t("switchgear.switchgear")} 1`,
              panels: props.partialResultReducer.state.panels,
              switchGearManufacturer: props.partialResult.dataValue.MetaData.SwitchGearGroup.Manufacturer,
              assetType: props.partialResult.dataValue.MetaData.SwitchGearGroup.Type,
              ratedVoltage: props.partialResult.dataValue.MetaData.SwitchGearGroup.RatedVoltage,
              operatingVoltage: props.partialResult.dataValue.MetaData.SwitchGearGroup.OperatingVoltage,
              installDate: Date.parse(props.partialResult.dataValue.MetaData.SwitchGearGroup.InstallDate)
                ? new Date(props.partialResult.dataValue.MetaData.SwitchGearGroup.InstallDate)
                : undefined,
              insulation: props.partialResult.dataValue.MetaData.SwitchGearGroup.Insulation,
            },
          ]
        : // For version 2 it includes multiple HV surveyable assets so map through all them to get the data.
          createMultipleSwitchGears(
            props.partialResult.dataValue.Assets,
            props.partialResult.dataValue.MetaData.HvSurveyableAssetGroups,
          ),
  };

  function onResubmitResultSuccess() {
    props.partialResultReducer.incrementStep();
  }

  const onResubmitButtonClick = useCallback(() => {
    if (props.partialResultReducer.state.isLastStep && props.partialResultReducer.state.stepIsValid) {
      const resubmissionObject = props.partialResultReducer.getResubmissionObject();
      resubmissionObject && resultResubmission.mutate(resubmissionObject);
    } else {
      setHighlightingInvalidField(true);
    }
  }, [props.partialResultReducer, resultResubmission]);

  const onBackButtonClick = useCallback(() => {
    resultResubmission.reset();
    props.partialResultReducer.decrementStep();
  }, [props.partialResultReducer, resultResubmission]);

  const validateSwitchgears = useCallback(() => {
    if (props.partialResult.dataValue.Assets) {
      const invalidIndicies: number[] = props.partialResult.dataValue.Assets.map(
        (switchgear: ResultSurveyableAsset, index: number) => {
          const selectedSwitchgear: PartialResultSwitchgear | undefined =
            props.partialResultReducer.state.selectedSwitchgears?.[index];
          return !selectedSwitchgear?.assetId;
        },
      ).reduce((acc: number[], _isInvalid: boolean, index: number) => {
        if (_isInvalid) acc.push(index);
        return acc;
      }, [] as number[]);

      setInvalidSwitchgears(invalidIndicies);
    }
  }, [
    props.partialResult.dataValue.Assets,
    props.partialResultReducer.state.selectedSwitchgears,
    setInvalidSwitchgears,
  ]);

  const onNextButtonClick = useCallback(() => {
    if (props.partialResultReducer.state.activeStep.step === Step.SelectSwitchgear) validateSwitchgears();
    if (props.partialResultReducer.state.stepIsValid && invalidSwitchgears.length === 0) {
      setHighlightingInvalidField(false);
      props.partialResultReducer.incrementStep();
    } else {
      setHighlightingInvalidField(true);
    }
  }, [props.partialResultReducer, invalidSwitchgears, validateSwitchgears]);

  const panels = useChildMappingsByAssetIds(
    (props.partialResultReducer.state.selectedSwitchgears ?? []).map((asset) => asset.assetId),
  ).data;

  useEffect(() => {
    if (!panels) return;

    const newPanels = panels.map(
      (panel) =>
        ({
          parentAssetId: panel.parentId,
          panelAssetId: panel.subjectId,
          panelNo: panel.panelNo,
        }) as PartialResultPanel,
    );

    const numberOfPanelsWithAssetId =
      props.partialResultReducer.state.selectedSwitchgears
        ?.map((s) => s.panels)
        .flat()
        .filter((p) => p?.panelAssetId != null).length ?? 0;
    const isOnPanelSelectionStep = props.partialResultReducer.state.activeStep.step === Step.SelectPanels;

    if (numberOfPanelsWithAssetId !== newPanels.length && isOnPanelSelectionStep) {
      props.partialResultReducer.updateAllSwitchgearsPanelAssetIds(newPanels);
    }
  }, [panels, props.partialResultReducer]);

  const onSwitchgearSelection = (id?: number, name?: string, index?: number) => {
    index = index ?? 0;

    const switchgear: PartialResultSwitchgear = {
      assetId: id ?? 0,
      name: name ?? "",
      assetType: getAssetTypeKey(props.partialResult.dataValue.Assets[index].AssetType),
      panels: props.partialResult.dataValue.Assets[index].PanelMeasurements.map(
        (p: PanelMeasurement): PartialResultPanel => ({
          panelAssetId: p.Panel360AssetId,
          panelNo: p.PanelNo,
          name: p.PanelName,
          isExcluded: false,
        }),
      ),
    };

    const switchgears = props.partialResultReducer.state.selectedSwitchgears || [];
    switchgears[index] = switchgear;
    props.partialResultReducer.setSwitchgears(switchgears);
  };

  const renderSwitchgearDropdown = (switchgear: ResultSurveyableAsset, index: number) => {
    const isInvalid = invalidSwitchgears.includes(index);
    return (
      <div key={index}>
        <Typography variant="caption" color="textSecondary">
          {switchgear.AssetName}
        </Typography>
        <SwitchgearDropdown
          onSwitchgearChange={onSwitchgearSelection}
          substationId={props.partialResultReducer.state.selectedSubstation?.id}
          selectedSwitchgearId={props.partialResultReducer.state.selectedSwitchgears?.[index]?.assetId}
          invalid={isInvalid}
          invalidMessage={t("common.requiredFieldValidationError", {
            val: "$t(asset.asset)",
          })}
          createable
          switchgearCreationPrepopulatedPanels={
            props.partialResultReducer.state.selectedSwitchgears?.find(
              (s: PartialResultSwitchgear) =>
                s.assetId === switchgear.AssetId && s.assetType === getAssetTypeKey(switchgear.AssetType),
            )?.panels
          }
          customerId={props.partialResultReducer.state.selectedCustomer?.id}
          index={index}
          assetType={switchgear.AssetType}
        />
      </div>
    );
  };

  function renderStepContent() {
    switch (props.partialResultReducer.state.activeStep.step) {
      case Step.SelectCustomer:
        return (
          <CustomerDropdown
            selectedCustomer={props.partialResultReducer.state.selectedCustomer}
            onCustomerSelect={props.partialResultReducer.setCustomer}
            onSingleCustomerAvailable={props.partialResultReducer.setCustomer}
            invalid={!props.partialResultReducer.state.stepIsValid && highlightingInvalidField}
            invalidMessage={t("common.requiredFieldValidationError", {
              val: "$t(customer.customer)",
            })}
            displaySingleCustomerName={true}
          />
        );
      case Step.SelectSubstation:
        return (
          <>
            <CompanyDropdown
              customerId={props.partialResultReducer.state.selectedCustomer?.id}
              selectedCompanyName={selectedCompanyName}
              onCompanyChange={handleCompanyChange}
              showLabel
              showClear
            />
            <br />
            <SiteDropdown
              companyId={companyId}
              customerId={props.partialResultReducer.state.selectedCustomer?.id}
              selectedSiteName={selectedSiteName}
              onSiteChange={handleSiteChange}
              showLabel
              showClear
            />
            <br />
            <HVSubstationDropdown
              onSubstationChange={props.partialResultReducer.setSubstation}
              customerId={props.partialResultReducer.state.selectedCustomer?.id}
              companyId={companyId}
              siteId={siteId}
              selectedSubstationId={props.partialResultReducer.state.selectedSubstation?.id}
              invalid={!props.partialResultReducer.state.stepIsValid && highlightingInvalidField}
              invalidMessage={t("common.requiredFieldValidationError", {
                val: "$t(substations.substation)",
              })}
              createable
              creationPrePopulatedValues={hvSubstationCreationPrePopulatedValues}
              showLabel
            />
          </>
        );
      case Step.SelectSwitchgear:
        if (!props.partialResult.dataValue.Assets || props.partialResult.dataValue.Assets.length === 0) {
          const switchgear = props.partialResultReducer.state.selectedSwitchgear;
          if (switchgear) {
            const currentSwitchgear = props.partialResultReducer.state.selectedSwitchgear;
            if (currentSwitchgear?.id !== switchgear.id || currentSwitchgear?.name !== switchgear.name) {
              props.partialResultReducer.setSwitchgear(switchgear.id, switchgear.name);
            }
          }
          return (
            <SwitchgearDropdown
              onSwitchgearChange={props.partialResultReducer.setSwitchgear}
              substationId={props.partialResultReducer.state.selectedSubstation?.id}
              selectedSwitchgearId={props.partialResultReducer.state.selectedSwitchgear?.id}
              invalid={!props.partialResultReducer.state.stepIsValid && highlightingInvalidField}
              invalidMessage={t("common.requiredFieldValidationError", {
                val: "$t(switchgear.switchgear)",
              })}
              createable
              switchgearCreationPrepopulatedPanels={props.partialResultReducer.state.panels}
              customerId={props.partialResultReducer.state.selectedCustomer?.id}
            />
          );
        } else {
          return props.partialResult.dataValue.Assets.map(renderSwitchgearDropdown);
        }
      case Step.SelectPanels:
        if (resultResubmission.isSuccess) return null;
        else
          return props.partialResultReducer.state.stepIsValid ? (
            <MessageCard
              className={classes.panelCheckerCard}
              messageType={MessageType.Success}
              content={t("panels.panelsMatched")}
            />
          ) : (
            <div className="w-100">
              <MessageCard
                highlightMessage={highlightingInvalidField}
                className={classes.panelCheckerCard}
                messageType={MessageType.Warning}
                title={t("panels.panelMatchingError")}
                secondaryText={t("panels.checkMissingPanels")}
                onMessageHighlightComplete={() => setHighlightingInvalidField(false)}
              />
            </div>
          );

      default:
        return "Unknown step";
    }
  }

  function renderStepActionButtons() {
    if (resultResubmission.isSuccess) return null;
    else
      return (
        <Stack direction="row" spacing={1} marginTop={2} justifyContent="flex-end">
          <div>
            <Button
              variant="contained"
              disabled={props.partialResultReducer.state.activeStep.index === 0}
              onClick={onBackButtonClick}
              className={classes.button}
            >
              {t("common.back")}
            </Button>
          </div>

          {props.partialResultReducer.state.isLastStep ? (
            <ProgressButton
              startIcon={<PublishIcon />}
              onClick={onResubmitButtonClick}
              disabled={resultResubmission.isLoading}
              loading={resultResubmission.isLoading}
              className={classes.noWrap}
            >
              {t("resultResubmission.resubmitResult")}
            </ProgressButton>
          ) : (
            <Button variant="contained" color="primary" onClick={onNextButtonClick} className={classes.button}>
              {t("common.next")}
            </Button>
          )}
        </Stack>
      );
  }

  function getStepSubtitle(step: Step) {
    switch (step) {
      case Step.SelectCustomer:
        return props.partialResultReducer.state.selectedCustomer?.name;
      case Step.SelectSubstation:
        return props.partialResultReducer.state.selectedSubstation?.name;
      case Step.SelectSwitchgear:
        return props.partialResultReducer.state.selectedSwitchgear?.name;
      case Step.SelectPanels:
        return resultResubmission.isSuccess ? t("panels.panelsOk") : ""; //only displaying this message when the stepper has moved on to the "next" step, which is successful resubmission
      default:
        return "Unknown step";
    }
  }

  return (
    <Card className={classes.root} variant="outlined">
      <CardContent>
        <Typography color="textSecondary" gutterBottom variant="subtitle2" className={classes.title} align="center">
          {t("results.partialResultSelectAssets")}
        </Typography>
        <Stepper
          activeStep={props.partialResultReducer.state.activeStep.index}
          className={classes.stepper}
          orientation="vertical"
        >
          {props.partialResultReducer.state.steps.map((step) => {
            const stepProps: { completed?: boolean } = {};
            const labelProps: { optional?: React.ReactNode } = {};

            if (step !== props.partialResultReducer.state.activeStep.step) {
              const stepSubtitle = getStepSubtitle(step);

              if (stepSubtitle) {
                labelProps.optional = (
                  <Typography variant="caption" color="textSecondary">
                    {stepSubtitle}
                  </Typography>
                );
              }
            }

            return (
              <MuiStep key={step} {...stepProps}>
                <StepLabel {...labelProps}>{t(step)}</StepLabel>
                <StepContent>
                  <div className={classes.contentContainer}>{renderStepContent()}</div>
                  {renderStepActionButtons()}
                </StepContent>
              </MuiStep>
            );
          })}
        </Stepper>

        <div className={classes.resubmissionStatusContainer}>
          {resultResubmission.isError && (
            <MessageToast
              text={t("common.dataError", {
                val: "$t(resultResubmission.resubmitResult)",
              })}
              mode={ToastMode.Error}
              show
            />
          )}
          {resultResubmission.isSuccess && (
            <MessageCard
              className={classes.panelCheckerCard}
              messageType={MessageType.Success}
              title={t("resultResubmission.resultResubmitted")}
              content={t("resultResubmission.resultWillBeReprocessed")}
              secondaryText={
                <div>
                  <p> {t("resultResubmission.resubmittedIfSuccessful")} </p>
                  <p> {t("resultResubmission.resubmittedIfError")}</p>
                </div>
              }
              cardActions={
                <Button
                  size="small"
                  color="primary"
                  className={classes.textButton}
                  component={Link}
                  to={resultPagePath || ""}
                >
                  {t("results.backToResultsList")}
                </Button>
              }
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
